// Five KPI tiles — Active, Total, Overdue, Hours, Spools complete/total.
// Mirrors the mockup .kpi-card grid at production-schedule.html line 692.
import { useMemo } from "react";
import type {
  JobDto,
  StageDefDto,
} from "../../../../../../shared/types/production-schedule";
import { isOverdue } from "../lib/dates";
import { isSpoolComplete } from "../lib/stageHelpers";
import { useActiveStages } from "./StagesProvider";

interface Props {
  jobs: JobDto[];
}

interface Stats {
  total: number;
  active: number;
  overdue: number;
  totalHours: number;
  spoolsDone: number;
  spoolsTotal: number;
}

function compute(jobs: JobDto[], catalogue: StageDefDto[]): Stats {
  let total = 0;
  let active = 0;
  let overdue = 0;
  let totalHours = 0;
  let spoolsDone = 0;
  let spoolsTotal = 0;
  for (const j of jobs) {
    total += 1;
    if (j.status === "In Progress" || j.status === "Scheduled") active += 1;
    if (isOverdue(j.dueDate, j.status)) overdue += 1;
    for (const s of j.spools) {
      spoolsTotal += 1;
      totalHours += s.hours;
      if (isSpoolComplete({ spool: s, catalogue, jobBay: j.bay })) spoolsDone += 1;
    }
  }
  return { total, active, overdue, totalHours, spoolsDone, spoolsTotal };
}

export function KpiCards({ jobs }: Props) {
  const catalogue = useActiveStages();
  const s = useMemo(() => compute(jobs, catalogue), [jobs, catalogue]);
  return (
    <div className="kpi-grid">
      <Tile label="Active Jobs" value={s.active} tone="blue" />
      <Tile label="Total Jobs" value={s.total} />
      <Tile
        label="Overdue"
        value={s.overdue}
        tone={s.overdue > 0 ? "red" : undefined}
      />
      <Tile
        label="Workshop Hours"
        value={s.totalHours.toFixed(1)}
        tone="orange"
        suffix="h"
      />
      <Tile
        label="Spools"
        value={`${s.spoolsDone}`}
        tone="green"
        suffix={`/${s.spoolsTotal}`}
      />
    </div>
  );
}

function Tile(props: {
  label: string;
  value: number | string;
  tone?: "blue" | "green" | "red" | "orange";
  suffix?: string;
}) {
  return (
    <div className="kpi-card">
      <div className="kpi-label">{props.label}</div>
      <div className={`kpi-val ${props.tone ?? ""}`}>
        {props.value}
        {props.suffix && (
          <span
            style={{
              fontSize: 13,
              color: "var(--bsp-fg-muted)",
              fontWeight: 600,
              marginLeft: 4,
            }}
          >
            {props.suffix}
          </span>
        )}
      </div>
    </div>
  );
}
